import type {
  CancelOptions,
  DeliveredNotifications,
} from '@capacitor/local-notifications';

// fixed local-notification ID reserved for the rest timer. chosen high to
// avoid collisions with NotificationService's hashed scheduled-reminder IDs
// (those are computed via DJB2 mod 2^31 but the chance of collision with one
// specific literal is astronomically low, and a collision would at worst
// replace a pending reminder).
export const REST_TIMER_NOTIFICATION_ID = 987654321;

// the small slice of the Capacitor LocalNotifications plugin that the rest-timer
// clear path touches. depending on this interface (rather than the whole plugin
// object) keeps clearRestTimerNotificationAsync unit-testable with a tiny fake —
// the real `LocalNotifications` satisfies it structurally, so callers pass it
// straight through with no cast.
export interface RestTimerNotificationPlugin {
  cancel(options: CancelOptions): Promise<void>;
  getDeliveredNotifications(): Promise<DeliveredNotifications>;
  removeDeliveredNotifications(delivered: DeliveredNotifications): Promise<void>;
}

// clear the rest timer's "Rest over" local notification wherever it can live —
// both the still-pending (scheduled) copy and any already-delivered tray entry.
export async function clearRestTimerNotificationAsync(
  plugin: RestTimerNotificationPlugin,
): Promise<void> {
  // remove the still-PENDING (scheduled, not yet fired) rest-timer notification
  await plugin.cancel({ notifications: [{ id: REST_TIMER_NOTIFICATION_ID }] });
  // also remove an already-DELIVERED tray entry from a prior set. cancel() only
  // clears pending notifications, so once the "Rest over" alert has fired into
  // the tray it lingers there showing the OLD exercise until the next rest fires
  // and replaces it (same id). starting a new set therefore left the first
  // exercise's notification stuck in the shade (#4084). mirror
  // NotificationService's delivered-cleanup, scoped to the rest timer's own id.
  const delivered = await plugin.getDeliveredNotifications();
  const stale = delivered.notifications.filter(n => n.id === REST_TIMER_NOTIFICATION_ID);
  // if any stale rest-timer entry is still showing, sweep it from the tray
  if (stale.length > 0) {
    await plugin.removeDeliveredNotifications({ notifications: stale });
  }
}
